import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../services/api';
import AdminShell from './AdminShell';

const crumbs = [
  { label: 'Dashboard', href: '/admin' },
  { label: 'Agents', href: '/admin/agents'  },
  { label: 'Logs', href: '/admin/logs'  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtMs(ms) {
  if (ms == null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

// ── Summary popup ─────────────────────────────────────────────────────────────

function SummaryCell({ text }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!text) return <span style={{ color: 'var(--ink-mute)', fontSize: '12px' }}>—</span>;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
          borderRadius: '4px', padding: '2px 8px', fontSize: '11px',
          cursor: 'pointer', color: 'var(--ink-soft)', fontFamily: 'var(--sans)',
          maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', display: 'block',
        }}>
        {text.slice(0, 40)}{text.length > 40 ? '…' : ''}
      </button>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 200, top: '110%', left: 0,
          background: 'var(--white)', border: '1px solid var(--paper-3)',
          borderRadius: '8px', padding: '12px 14px', width: '280px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          fontSize: '13px', lineHeight: 1.6, color: 'var(--ink)',
        }}>
          {text}
        </div>
      )}
    </div>
  );
}

// ── Slide-over detail panel ───────────────────────────────────────────────────

function DetailPanel({ logId, onClose }) {
  const [log,     setLog]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('prompt'); // prompt | response | meta

  useEffect(() => {
    if (!logId) return;
    setLoading(true);
    setTab('prompt');
    apiFetch(`/api/admin/logs/${logId}`)
      .then(setLog)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [logId]);

  // Close on Escape
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const tabs = ['prompt', 'response', 'meta'];

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)',
        zIndex: 300, backdropFilter: 'blur(2px)',
      }} />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(600px, 100vw)',
        background: 'var(--white)', zIndex: 301,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
        animation: 'slideIn 0.2s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--paper-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: '18px', fontWeight: '400' }}>
              Log #{logId}
            </div>
            {log && (
              <div style={{ fontSize: '12px', color: 'var(--ink-mute)', marginTop: '2px' }}>
                {fmt(log.created_at)}
                {log.error && (
                  <span style={{ marginLeft: '8px', color: '#c8563a', fontWeight: '600' }}>
                    ● Error
                  </span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid var(--paper-3)',
            borderRadius: '6px', padding: '6px 10px', cursor: 'pointer',
            fontSize: '14px', color: 'var(--ink-mute)',
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: '2px', padding: '10px 20px 0',
          borderBottom: '1px solid var(--paper-3)', flexShrink: 0,
        }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '7px 14px', fontSize: '12px', fontWeight: '500',
              fontFamily: 'var(--sans)', cursor: 'pointer', textTransform: 'capitalize',
              border: 'none', borderBottom: tab === t
                ? '2px solid var(--ink)' : '2px solid transparent',
              background: 'none', color: tab === t ? 'var(--ink)' : 'var(--ink-mute)',
              marginBottom: '-1px',
            }}>
              {t === 'prompt' ? 'Prompt' : t === 'response' ? 'Response' : 'Meta'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {loading ? (
            <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>Loading…</p>
          ) : !log ? (
            <p style={{ color: 'var(--accent)', fontSize: '13px' }}>Not found.</p>
          ) : (
            <>
              {tab === 'prompt' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {log.system_prompt && (
                    <TextBlock label="System Prompt" text={log.system_prompt} />
                  )}
                  <TextBlock label="User Prompt" text={log.user_prompt} />
                  {log.summary && (
                    <TextBlock label="Summary" text={log.summary} />
                  )}
                </div>
              )}
              {tab === 'response' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {log.error ? (
                    <div style={{
                      background: 'rgba(200,86,58,0.08)',
                      border: '1px solid rgba(200,86,58,0.3)',
                      borderRadius: '8px', padding: '14px 16px',
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: '600',
                        color: '#c8563a', marginBottom: '6px',
                        textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Error
                      </div>
                      <pre style={{ fontFamily: 'monospace', fontSize: '12px',
                        color: '#a03020', whiteSpace: 'pre-wrap',
                        margin: 0, lineHeight: 1.6 }}>
                        {log.error}
                      </pre>
                    </div>
                  ) : null}
                  {log.response_text ? (
                    <TextBlock label="Response" text={log.response_text} mono={false} />
                  ) : (
                    <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>
                      No response text.
                    </p>
                  )}
                </div>
              )}
              {tab === 'meta' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    ['Provider',      log.provider],
                    ['Model',         log.model],
                    ['Purpose',       log.purpose ?? '—'],
                    ['Input tokens',  log.input_tokens  ?? '—'],
                    ['Output tokens', log.output_tokens ?? '—'],
                    ['Latency',       fmtMs(log.latency_ms)],
                    ['Created',       fmt(log.created_at)],
                    ['Log ID',        log.id],
                  ].map(([label, value]) => (
                    <div key={label} style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', gap: '16px',
                      padding: '10px 14px', background: 'var(--paper-2)',
                      borderRadius: '6px',
                    }}>
                      <span style={{ fontSize: '12px', color: 'var(--ink-mute)',
                        flexShrink: 0 }}>{label}</span>
                      <span style={{ fontSize: '13px', fontWeight: '500',
                        wordBreak: 'break-all', textAlign: 'right' }}>
                        {String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

function TextBlock({ label, text, mono = true }) {
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--ink-mute)',
        marginBottom: '8px' }}>
        {label}
      </div>
      <pre style={{
        background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
        borderRadius: '6px', padding: '12px 14px', margin: 0,
        fontFamily: mono ? 'monospace' : 'var(--sans)',
        fontSize: '12px', lineHeight: 1.7, color: 'var(--ink)',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        overflowX: 'auto',
      }}>
        {text}
      </pre>
    </div>
  );
}

// ── Main list page ────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  date_from: '', date_to: '',
  provider: '', model: '', purpose: '',
  has_error: '',
};

export default function AdminLogListPage() {
  const { user }   = useAuth();
  const navigate   = useNavigate();

  const [logs,       setLogs]       = useState([]);
  const [meta,       setMeta]       = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [filters,    setFilters]    = useState(EMPTY_FILTERS);
  const [applied,    setApplied]    = useState(EMPTY_FILTERS);
  const [page,       setPage]       = useState(1);
  const [filterOpts, setFilterOpts] = useState({ providers: [], models: [], purposes: [] });
  const [activeLog,  setActiveLog]  = useState(null); // id for slide-over

  useEffect(() => {
    if (user && !user.isSuperAdmin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Load filter dropdown options once
  useEffect(() => {
    apiFetch('/api/admin/logs/filters')
      .then(setFilterOpts)
      .catch(() => {});
  }, []);

  const fetchLogs = useCallback((appliedFilters, pg) => {
    setLoading(true);
    const params = new URLSearchParams({ page: pg });
    if (appliedFilters.date_from) params.set('date_from', appliedFilters.date_from);
    if (appliedFilters.date_to)   params.set('date_to',   appliedFilters.date_to);
    if (appliedFilters.provider)  params.set('provider',  appliedFilters.provider);
    if (appliedFilters.model)     params.set('model',     appliedFilters.model);
    if (appliedFilters.purpose)   params.set('purpose',   appliedFilters.purpose);
    if (appliedFilters.has_error === 'true')  params.set('has_error', 'true');
    if (appliedFilters.has_error === 'false') params.set('has_error', 'false');

    apiFetch(`/api/admin/logs?${params}`)
      .then(data => {
        setLogs(data.items ?? []);
        setMeta({ total: data.total, page: data.page, total_pages: data.total_pages });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLogs(applied, page); }, [applied, page, fetchLogs]);

  function applyFilters() { setPage(1); setApplied({ ...filters }); }
  function clearFilters()  { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1); }

  const hasActive = Object.values(applied).some(v => v !== '');

  return (
    <AdminShell crumbs={crumbs}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: '24px',
            fontWeight: '400', marginBottom: '2px' }}>
            LLM Audit Logs
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>
            {meta.total.toLocaleString()} total records
          </p>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div style={{
        background: 'var(--white)', border: '1px solid var(--paper-3)',
        borderRadius: 'var(--r)', padding: '14px 16px', marginBottom: '16px',
      }}>
        {/* Row 1: dates */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap',
          marginBottom: '10px', alignItems: 'flex-end' }}>
          <FilterField label="From">
            <input type="date" value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
              style={inputStyle} />
          </FilterField>
          <FilterField label="To">
            <input type="date" value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
              style={inputStyle} />
          </FilterField>

          <FilterField label="Provider">
            <select value={filters.provider}
              onChange={e => setFilters(f => ({ ...f, provider: e.target.value }))}
              style={inputStyle}>
              <option value="">All</option>
              {filterOpts.providers.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </FilterField>

          <FilterField label="Model">
            <select value={filters.model}
              onChange={e => setFilters(f => ({ ...f, model: e.target.value }))}
              style={inputStyle}>
              <option value="">All</option>
              {filterOpts.models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </FilterField>

          <FilterField label="Purpose">
            <select value={filters.purpose}
              onChange={e => setFilters(f => ({ ...f, purpose: e.target.value }))}
              style={inputStyle}>
              <option value="">All</option>
              {filterOpts.purposes.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </FilterField>

          <FilterField label="Errors">
            <select value={filters.has_error}
              onChange={e => setFilters(f => ({ ...f, has_error: e.target.value }))}
              style={inputStyle}>
              <option value="">All</option>
              <option value="true">Errors only</option>
              <option value="false">No errors</option>
            </select>
          </FilterField>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end',
            paddingBottom: '0' }}>
            <button onClick={applyFilters} style={primaryBtn}>Apply</button>
            {hasActive && (
              <button onClick={clearFilters} style={ghostBtn}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>Loading…</p>
      ) : logs.length === 0 ? (
        <div style={{
          background: 'var(--white)', border: '1px solid var(--paper-3)',
          borderRadius: 'var(--r)', padding: '48px', textAlign: 'center',
        }}>
          <p style={{ color: 'var(--ink-mute)', fontSize: '14px' }}>
            No logs found{hasActive ? ' for these filters' : ''}.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div style={{
            background: 'var(--white)', border: '1px solid var(--paper-3)',
            borderRadius: 'var(--r)', overflow: 'hidden',
            display: 'none',
          }} className="log-table-desktop">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--paper-3)',
                  background: 'var(--paper-2)' }}>
                  {['Date', 'Provider', 'Model', 'Purpose', 'Tokens', 'Latency',
                    'Summary', ''].map(h => (
                    <th key={h} style={{
                      padding: '9px 12px', textAlign: 'left',
                      fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: 'var(--ink-mute)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => {
                  const hasErr = !!log.error;
                  return (
                    <tr key={log.id}
                      style={{
                        borderBottom: i < logs.length - 1
                          ? '1px solid var(--paper-3)' : 'none',
                        background: hasErr ? 'rgba(200,86,58,0.04)' : 'transparent',
                      }}>
                      <td style={td}>
                        <span style={{ fontSize: '12px', color: 'var(--ink-soft)',
                          whiteSpace: 'nowrap' }}>
                          {fmt(log.created_at)}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: '12px' }}>{log.provider}</span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: '12px', fontFamily: 'monospace',
                          color: 'var(--ink-soft)' }}>
                          {log.model}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: '12px', color: 'var(--ink-soft)' }}>
                          {log.purpose ?? '—'}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: '12px', color: 'var(--ink-soft)',
                          whiteSpace: 'nowrap' }}>
                          {log.input_tokens != null && log.output_tokens != null
                            ? `${log.input_tokens} / ${log.output_tokens}`
                            : '—'}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: '12px', color: 'var(--ink-soft)' }}>
                          {fmtMs(log.latency_ms)}
                        </span>
                      </td>
                      <td style={td}>
                        {hasErr ? (
                          <span style={{ fontSize: '11px', fontWeight: '600',
                            color: '#c8563a', background: 'rgba(200,86,58,0.1)',
                            padding: '2px 8px', borderRadius: '4px' }}>
                            ● Error
                          </span>
                        ) : (
                          <SummaryCell text={log.summary} />
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => setActiveLog(log.id)}
                          style={viewBtn}>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="log-cards-mobile" style={{ display: 'flex',
            flexDirection: 'column', gap: '10px' }}>
            {logs.map(log => {
              const hasErr = !!log.error;
              return (
                <div key={log.id} style={{
                  background: 'var(--white)',
                  border: `1px solid ${hasErr ? 'rgba(200,86,58,0.35)' : 'var(--paper-3)'}`,
                  borderRadius: 'var(--r)', padding: '14px 16px',
                  background: hasErr ? 'rgba(200,86,58,0.03)' : 'var(--white)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>
                        {log.provider} / <span style={{ fontFamily: 'monospace',
                          fontSize: '12px' }}>{log.model}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--ink-mute)',
                        marginTop: '2px' }}>
                        {fmt(log.created_at)}
                      </div>
                    </div>
                    {hasErr && (
                      <span style={{ fontSize: '11px', fontWeight: '600',
                        color: '#c8563a', background: 'rgba(200,86,58,0.1)',
                        padding: '2px 8px', borderRadius: '4px', flexShrink: 0 }}>
                        ● Error
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap',
                    marginBottom: '10px' }}>
                    {log.purpose && (
                      <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
                        {log.purpose}
                      </span>
                    )}
                    {(log.input_tokens != null || log.output_tokens != null) && (
                      <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
                        tokens: {log.input_tokens ?? '?'}/{log.output_tokens ?? '?'}
                      </span>
                    )}
                    {log.latency_ms != null && (
                      <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
                        {fmtMs(log.latency_ms)}
                      </span>
                    )}
                  </div>
                  {log.summary && (
                    <p style={{ fontSize: '12px', color: 'var(--ink-soft)',
                      marginBottom: '10px', lineHeight: 1.5 }}>
                      {log.summary.slice(0, 120)}{log.summary.length > 120 ? '…' : ''}
                    </p>
                  )}
                  <button onClick={() => setActiveLog(log.id)} style={viewBtn}>
                    View detail →
                  </button>
                </div>
              );
            })}
          </div>

          {/* ── Pagination ── */}
          <div style={{ display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', marginTop: '16px',
            flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--ink-mute)' }}>
              Page {meta.page} of {meta.total_pages}
              {' · '}{meta.total.toLocaleString()} records
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ ...ghostBtn, opacity: page === 1 ? 0.4 : 1 }}>
                ← Prev
              </button>
              {/* Page number pills — show up to 5 around current */}
              {Array.from({ length: meta.total_pages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === meta.total_pages
                  || Math.abs(p - page) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '…' ? (
                    <span key={`ellipsis-${i}`} style={{ padding: '6px 4px',
                      fontSize: '12px', color: 'var(--ink-mute)' }}>…</span>
                  ) : (
                    <button key={p} onClick={() => setPage(p)} style={{
                      ...ghostBtn,
                      background: p === page ? 'var(--ink)' : 'transparent',
                      color:      p === page ? 'var(--paper)' : 'var(--ink-soft)',
                      borderColor: p === page ? 'var(--ink)' : undefined,
                      minWidth: '32px',
                    }}>{p}</button>
                  )
                )
              }
              <button
                onClick={() => setPage(p => Math.min(meta.total_pages, p + 1))}
                disabled={page === meta.total_pages}
                style={{ ...ghostBtn, opacity: page === meta.total_pages ? 0.4 : 1 }}>
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      {/* Slide-over */}
      {activeLog && (
        <DetailPanel logId={activeLog} onClose={() => setActiveLog(null)} />
      )}

      {/* Responsive styles */}
      <style>{`
        @media (min-width: 640px) {
          .log-table-desktop { display: block !important; }
          .log-cards-mobile  { display: none   !important; }
        }
        @media (max-width: 639px) {
          .log-table-desktop { display: none   !important; }
          .log-cards-mobile  { display: flex   !important; }
        }
      `}</style>
    </AdminShell>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FilterField({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: '500',
        color: 'var(--ink-mute)', marginBottom: '4px', letterSpacing: '0.03em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle = {
  padding: '7px 10px', fontSize: '12px',
  border: '1px solid var(--paper-3)', borderRadius: '6px',
  fontFamily: 'var(--sans)', background: 'var(--white)',
  color: 'var(--ink)', boxSizing: 'border-box',
};

const td = {
  padding: '10px 12px',
  verticalAlign: 'middle',
};

const primaryBtn = {
  padding: '7px 16px', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: '6px', fontFamily: 'var(--sans)',
  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
};

const ghostBtn = {
  padding: '6px 12px', background: 'transparent',
  border: '1px solid var(--paper-3)', borderRadius: '6px',
  fontFamily: 'var(--sans)', fontSize: '12px',
  cursor: 'pointer', color: 'var(--ink-soft)',
};

const viewBtn = {
  padding: '4px 12px', background: 'var(--paper-2)',
  border: '1px solid var(--paper-3)', borderRadius: '5px',
  fontFamily: 'var(--sans)', fontSize: '12px',
  cursor: 'pointer', color: 'var(--ink-soft)',
};
